import { getRequiredBrowseData, minColumnWidth } from "#skyward_browse";
import { getBrowseById, getContainingBrowse, getCurrentBrowseName } from "#skyward_browseList";
import { virtualDir } from "#skyward_global";
import { registerLoadDOMCallback } from "#skyward_window";
import { save as browseSeekInfoSave } from "./skyward.ui.browseSeekInfo";
import { changeCheckBoxWithSelectAll, disable, enable, getValue, setValue } from "./skyward.ui.controls";
import { registerEvents } from "./skyward.ui.events";
import { addDisableNavigationPromise } from "./skyward.ui.workflow";
import { Browse, DOVSaveDataInput, JQueryInput, SaveData } from "./types";

export interface EditableBrowse extends Browse
{
    id: string;
    editableData: any;
    renderFilter: boolean;
    renderView: boolean;
    disableDataLossWarning: boolean;
    renderFooter: boolean;
    readOnly: boolean;
    currentTaskInstanceId: string;
    readOnlyHash: string;
    renderSortableHeader: boolean;
    renderHeaderSettings: boolean;
    renderControls: boolean;
    dataObjectViewerID: string;
}

const skyward = window.skyward;

let currentlySelectingAll = false;

const events =
{
    init,
    autosizeHeadersAndFooters: resizeHeadersAndFootersForContent
};

registerEvents("editableBrowse", events);

export function calculateTotalPercentage(browse: JQuery<HTMLElement>, controlToUpdate: JQuery<HTMLElement> | undefined, dataAttr: string, dataAttrValue: string)
{
    // Find control of label to update
    if (controlToUpdate?.get(0))
    {
        // Get the text of the label, If none is found default it
        const originalText = controlToUpdate.html() || "Total";
        const endIndex = originalText.lastIndexOf("-");
        let newText = originalText;

        if (endIndex > -1)
        {
            // Grab original label without percentage
            newText = originalText.substring(0, endIndex - 1);
        }

        let total = 0;
        // Add up all input with data attribute
        browse.find(`input[data-${dataAttr}='${dataAttrValue}']`).each(function ()
        {
            const value = parseFloat($(this).val() as string);
            if (value && !isNaN(value))
            {
                total += value;
            }
        });

        const displayTotal = new Number(total).toFixed(2);
        controlToUpdate.html(`${newText} - ${displayTotal}%`);
    }
}

export function getData(browse: EditableBrowse)
{
    const editableData = getEditableData(browse.id);

    const data =
    {
        editableData: editableData,
        renderFilter: $(`#${browse.id}_filterButton`).length > 0,
        renderView: $(`#${browse.id}_viewButton`).length > 0,
        disableDataLossWarning: browse.disableDataLossWarning,
        renderFooter: browse.renderFooter,
        readOnly: browse.readOnly,
        currentTaskInstanceId: browse.currentTaskInstanceId,
        readOnlyHash: browse.readOnlyHash,
        renderSortableHeader: browse.renderSortableHeader,
        renderHeaderSettings: browse.renderHeaderSettings,
        renderControls: browse.renderControls,
        dataObjectViewerID: browse.dataObjectViewerID
    } as unknown as EditableBrowse;

    return data;
}

export function getEditableData(browseId: string)
{
    return JSON.stringify($(`#${browseId}`).data("editablebrowsedata"));
}

export function isCurrentlySelectingAll()
{
    return currentlySelectingAll;
}

export function selectAllCheckBox(browseName: string, checkboxId: string, columnIndex: number)
{
    currentlySelectingAll = true;
    const domBrowse = $(`#${browseName}`);
    const selectAllCheckBox = domBrowse.find(`#${checkboxId}`);

    if (selectAllCheckBox.get(0))
    {
        if (selectAllCheckBox.hasClass("ui-disabled"))
        {
            return;
        }
        const isChecked = selectAllCheckBox.is(":checked");

        domBrowse.find(`td[data-column="${columnIndex}"]`).each(function ()
        {
            const cell = $(this);
            const currentCheckBox = cell.find('input:checkbox');

            if (currentCheckBox?.get(0))
            {
                changeCheckBoxWithSelectAll(currentCheckBox, isChecked);
            }
        }).promise().done(function ()
        {
            currentlySelectingAll = false;
            //Since multiple select all boxes can be on one screen this will need to be changed be gotten from the checkbox
            if (selectAllCheckBox.data("checkboxidformat") && selectAllCheckBox.data("checkboxidformat") !== "")
            {
                const browse = getBrowseById(browseName);
                if (!browse)
                {
                    return null;
                }

                const browseData = getRequiredBrowseData(browse, browse.data || domBrowse.data());
                const url = `${virtualDir}${browse.routeModule}/${browse.routeObject}/SelectAll`;

                disable(selectAllCheckBox);

                const editableBrowseDataLoaded = $.Deferred();
                addDisableNavigationPromise(editableBrowseDataLoaded, false);

                return $.ajax(
                    {
                        url: url,
                        type: "POST",
                        dataType: "JSON",
                        complete: function ()
                        {
                            enable(selectAllCheckBox);
                            editableBrowseDataLoaded.resolve();
                        },
                        success: function (allPrimaryKeys)
                        {
                            const editableBrowseData = domBrowse.data().editablebrowsedata || [];

                            $.each(allPrimaryKeys, function ()
                            {
                                const controlName = selectAllCheckBox.data("checkboxidformat").replace("{0}", this.toString());
                                let currentInput: JQuery<HTMLElement> | DOVSaveDataInput = domBrowse.find(`[name='${controlName}']`);
                                if (currentInput.length === 0)
                                {
                                    currentInput = { saveJson: true, name: controlName, value: isChecked, data: {} };
                                }
                                skyward.dataobjectviewer.updateCustomSaveData(currentInput, editableBrowseData, true);
                            });
                        },
                        data: browseData
                    });
            }

            return null;
        });
    }
    else
    {
        currentlySelectingAll = false;
    }
}

export function disableRow(rowElement: JQuery<HTMLElement>)
{
    rowElement.find("input").each(function ()
    {
        const input = $(this);
        setValue(input, input.data().originalValue);
        disable(input);
    });

    rowElement.find(".browseDataCell").addClass("disabled");
}

export function enableRow(rowElement: JQuery<HTMLElement>)
{
    rowElement.find("input").each(function ()
    {
        enable($(this));
    });

    rowElement.find(".browseDataCell").removeClass("disabled");
}

interface EditableBrowseData
{
    editablebrowsedata?: SaveData[];
    originalcustomsavedata?: { Key: string; Value: unknown; }[];
}

function init(selector: JQueryInput)
{
    const editableBrowse = $(selector);
    const data = editableBrowse.data() as EditableBrowseData;

    //This will trigger a scroll of locked browseBody also, required as rows can't be selected in editable browses
    editableBrowse.find(".unlockedBrowseBody").scrollTop(0);

    data.editablebrowsedata ??= [];

    const originalCustomSaveData = data.originalcustomsavedata ?? [];
    if (originalCustomSaveData.length)
    {
        const inputs: Record<string, JQuery<HTMLElement>> = {};
        editableBrowse.find("[name]").each(function (this)
        {
            inputs[(this as HTMLInputElement).name] = $(this);
        });

        $.each(originalCustomSaveData, function ()
        {
            let currentInput: JQuery<HTMLElement> | DOVSaveDataInput = inputs[this.Key];
            if (!currentInput)
            {
                currentInput = { saveJson: true, name: this.Key, value: this.Value, data: {} };
            }
            else
            {
                setValue(currentInput, this.Value, true);
                currentInput.attr("data-original-value", getValue(currentInput));
            }
            skyward.dataobjectviewer.updateCustomSaveData(currentInput, data.editablebrowsedata!, true);
        });
        editableBrowse.data("originalCustomSaveData", "");
        data.originalcustomsavedata = [];
    }

    editableBrowse.find("input, select, textarea, .dateTimePickerWrapper").not(".browseSearchBox, .browseRowsPerPage, .doNotPost, .doNotPost *").each(function ()
    {
        const input = $(this);
        input.on("change", function ()
        {
            skyward.dataobjectviewer.updateCustomSaveData(input, data.editablebrowsedata!, true);
            // TODO (6498001): this can't possibly be right, can it? Shouldn't it use the editable browse name and not getCurrentBrowseName()?
            browseSeekInfoSave(getBrowseById(getCurrentBrowseName())!);
        });

        // Add event to fire when checkbox is changed to reset percent field
        if (input.data("selectpercent"))
        {
            input.on("change", function ()
            {
                if (input.is(":checkbox") && !input.is(":checked"))
                {
                    setValue(editableBrowse.find(`#${input.attr("data-selectPercent")}`), "");
                }
            });
        }

        // Add event to fire when percent field with this data attribute is changed for browse label
        if (input.data("includefieldforsectionheaderpercenttotal"))
        {
            const calculatePercentageInSectionHeader = function ()
            {
                calculateTotalPercentage(editableBrowse, editableBrowse.find(".sectionHeader__text"), "includefieldforsectionheaderpercenttotal", "true");
            };

            input.on("change", calculatePercentageInSectionHeader);

            // Fire when DOM is loaded to calculate existing percentage if percenttotal control exist
            $(function ()
            {
                calculatePercentageInSectionHeader();
            });
        }

        // Add event to fire when percent field with this data attribute is changed for segmented row header
        if (input.data("includefieldforsegmentedrowheaderpercenttotal"))
        {
            const fieldName = input.data("includefieldforsegmentedrowheaderpercenttotal");
            const calculatePercentageInSegmentedRowHeader = function ()
            {
                // Add the field name to the class name too since you could have multiple segmented column that requires a running total
                calculateTotalPercentage(editableBrowse, editableBrowse.find(`div .parentDataCell.segmentedRowHeaderTotalPercent.${fieldName}`), "includefieldforsegmentedrowheaderpercenttotal", fieldName);
            };

            input.on("change", calculatePercentageInSegmentedRowHeader);

            // Fire when DOM is loaded to calculate existing percentage if percenttotal control exist
            $(function ()
            {
                calculatePercentageInSegmentedRowHeader();
            });
        }
    });

    setAccessibilityAndResizeBrowseHeadersFooters(editableBrowse);

    registerLoadDOMCallback((domLoadSelector: string) =>
    {
        if (editableBrowse.is(domLoadSelector))
        {
            setAccessibilityAndResizeBrowseHeadersFooters(editableBrowse);
        }
    });
}

function setAccessibilityAndResizeBrowseHeadersFooters(editableBrowse: JQuery<HTMLElement>)
{
    if (editableBrowse.is(":visible"))
    {
        // Populate aria-labelledby attribute with the browse's title
        editableBrowse.attr("aria-labelledby", editableBrowse.find(".sectionHeader__text").attr("id")!);
        resizeHeadersAndFootersForContent(editableBrowse);
    }
}

interface HeaderAndFooterResizeSize
{
    width: number;
    minWidth: number | null;
    header: JQuery<HTMLElement>;
    footer: JQuery<HTMLElement>;
}

interface CellResizeSize
{
    width: number;
    fitColumn: boolean;
    cells: JQuery<HTMLElement>;
}

function resizeHeadersAndFootersForContent(editableBrowse: JQuery<HTMLElement>)
{
    const containingBrowse = getContainingBrowse(editableBrowse);
    if (!containingBrowse)
    {
        return;
    }
    // These are header table-cells' sole children
    const autosizeHeaders = containingBrowse.header?.find(".js-browseHeader__cellContentContainer--autosize");
    if (autosizeHeaders && autosizeHeaders.length > 0)
    {
        autosizeHeaders.removeClass(".js-browseHeader__cellContentContainer--autosize");

        if (containingBrowse.browse && containingBrowse.browse.find(".noRecordsInBrowse").length > 0)
        {
            return;
        }

        const headerAndFooterResizeSizes: HeaderAndFooterResizeSize[] = [];
        const cellResizeSizes: CellResizeSize[] = [];
        autosizeHeaders.each(function ()
        {
            const column = $(this).data("column");
            const correspondingTotalFooterColumnDiv = containingBrowse.footer!.find(`div[data-column='${CSS.escape(column)}']`);
            const data = $(this).data();
            const preferredWidth = data.preferredWidth;
            const fitColumn: boolean = data.fitColumn;
            const columnCells = containingBrowse.body?.find(`.browseRow .browseDataCell.column${column} > div`);

            const cell = columnCells?.[0];
            if (!cell)
            {
                return;
            }

            const cellWidth = parseInt(getComputedStyle(cell).width, 10);
            // If the view has a width defined, it will be passed as data-preferred-width so we can measure the
            // header's intrinsic width.
            const headerWidth = typeof preferredWidth === "number"
                ? preferredWidth
                : parseInt(getComputedStyle(this).width, 10);
            const width = Math.max(headerWidth, cellWidth);

            // Don't allow resizing below the cell's width, unless the cell's viewpart supports it
            const minWidth = fitColumn
                ? null
                : Math.max(cellWidth, minColumnWidth);

            // Changing widths can change how the table's width is distributed among other rows.  All columns must have widths
            // defined to prevent discrepancies after we update them.
            headerAndFooterResizeSizes.push({ width: width, minWidth: minWidth, header: $(this), footer: correspondingTotalFooterColumnDiv });
            cellResizeSizes[column] = { width: width, fitColumn: fitColumn, cells: columnCells };
        });

        headerAndFooterResizeSizes.forEach(function (headerObj)
        {
            const header = headerObj.header;
            const footer = headerObj.footer;
            header[0].style.width = `${headerObj.width}px`;
            if (footer.length > 0)
            {
                footer[0].style.width = `${headerObj.width}px`;
            }
            if (headerObj.minWidth && header.is(".tdResizeDiv.ui-resizable"))
            {
                header.resizable("option", "minWidth", headerObj.minWidth);
            }
        });

        cellResizeSizes.forEach(function (columnObj)
        {
            // Tables are constrained to grid layout, and normally we would only have to set one row's width.
            // However, any children with parent-relative widths, such as percentages, appear to assume the width
            // the column would have had otherwise.
            // This could be avoided by setting the parent td's width instead.
            columnObj.cells.css("width", columnObj.width);
            if (columnObj.fitColumn)
            {
                columnObj.cells.children().css("width", "100%");
            }
        });
    }
}
